import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { buildProfile } from '@tutlo/core'
import { Button } from '@tutlo/ui'
import { useEffect, useRef } from 'react'
import { AskFooter, Shell } from '../layout.tsx'
import { useBooking, useResumeSession, useSlots } from '../lead.ts'
import { ProfileCard } from '../screens/konto/profile-card.tsx'
import { bookingState } from '../screens/start/booking-state.ts'
import { MeetingDemoCard } from '../screens/start/meeting-demo.tsx'
import { ActionTiles, PermaLinkNote } from '../screens/start/next-steps.tsx'
import { PlatformEntry } from '../screens/start/platform-entry.tsx'
import { PrepSteps } from '../screens/start/prep-steps.tsx'
import { StartAside } from '../screens/start/start-aside.tsx'
import { StatusCard } from '../screens/start/status-card.tsx'
import { validateSearch } from '../search.ts'

export const Route = createFileRoute('/s/$token')({
  validateSearch,
  component: StartRoute,
})

/**
 * Poczekalnia (Figma `449:849`, D7): stały link leada. Wejście = `POST /api/session` (cookie z
 * tokena). Stan z terminem = start przed lekcją; stan bez terminu = landing remarketingowy
 * (e-mail jest, telefonu/rezerwacji brak) z dokończeniem zapisu. `?widok=przygotowanie` = kroki.
 * Deep links for mail/SMS CTAs: `?widok=kalendarz` goes straight to booking (unless a booking is
 * already active), `?widok=czat` to the chat; both leave a clean `/s/:token` in history.
 */
function StartRoute() {
  const { token } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const resume = useResumeSession()
  const { mutate } = resume
  const key = search.k
  // Read once on mount: the deep link is consumed (and cleaned from the URL) after the session
  // and the booking state are known, so a later `?widok=` change must not re-trigger it.
  const deepLink = useRef(
    search.widok === 'kalendarz' || search.widok === 'czat' ? search.widok : null,
  )
  // One resume per token: the `?k=` inbox key from the e-mail link is dropped from the address
  // bar right after the session is set (history, screenshots), which must not resume again.
  const resumedToken = useRef<string | null>(null)
  useEffect(() => {
    if (resumedToken.current === token) return
    resumedToken.current = token
    mutate(
      { token, key },
      {
        onSuccess: () => {
          // With a deep link the redirect effect below owns the URL cleanup.
          if (key && !deepLink.current) {
            navigate({ to: '/s/$token', params: { token }, search: {}, replace: true })
          }
        },
      },
    )
  }, [mutate, navigate, token, key])
  const lead = resume.data ?? null
  const booking = useBooking(lead?.lastBookingId ?? '')
  const slots = useSlots(3)
  const bookingReady = !!lead && !(lead.lastBookingId && booking.isPending)

  // `active` = a booking the visitor can still act on (upcoming or attended); a past, missed or
  // cancelled one drops to the rebook state, copy from `bookingState`.
  const last = booking.data ?? null
  const state = bookingState(last)
  const active = last && (state.kind === 'upcoming' || state.kind === 'completed') ? last : null

  useEffect(() => {
    const target = deepLink.current
    if (!target || !bookingReady) return
    deepLink.current = null
    const run = async () => {
      // Replace first, then push: "back" from the calendar or chat lands on the clean waiting room.
      await navigate({ to: '/s/$token', params: { token }, search: {}, replace: true })
      if (target === 'kalendarz' && active) return
      await navigate({ to: target === 'czat' ? '/chat' : '/kalendarz', search: {} })
    }
    void run()
  }, [bookingReady, active, navigate, token])

  if (resume.isError) {
    navigate({ to: '/', replace: true })
    return null
  }
  if (!bookingReady) return <Shell nav={{}}>{null}</Shell>
  const bookCta = state.kind === 'rebook' ? state.cta : 'Wybierz termin spotkania'
  const nearestStart = slots.data?.slots[0]?.start ?? null
  const durationMin = active
    ? Math.max(1, Math.round((Date.parse(active.endAt) - Date.parse(active.startAt)) / 60_000))
    : (slots.data?.durationMin ?? 30)
  const toBooking = () => navigate({ to: '/kalendarz', search: {} })
  const toChange = () =>
    active ? navigate({ to: '/kalendarz', search: { widok: 'dni', zmiana: active.id } }) : undefined
  const backHome = () => navigate({ to: '/s/$token', params: { token }, search: {} })

  if (search.widok === 'przygotowanie') {
    return (
      <Shell
        nav={{ back: backHome }}
        footer={<AskFooter />}
        // Desktop (Figma `642:3552`): the booking summary aside carries the CTAs, so the dock
        // CTA below is mobile-only.
        aside={
          <StartAside
            booking={last}
            lead={lead}
            durationMin={durationMin}
            nearestStart={nearestStart}
            onBook={toBooking}
            onChange={toChange}
          />
        }
        cta={
          // Figma "Propozycja C": booked leads get the platform entry (the note above invites them
          // to test their setup there); a lead without a booking finishes the signup instead.
          <div className="desktop:hidden">
            {active ? (
              <PlatformEntry booking={active} lead={lead} brief />
            ) : (
              <Button className="w-full" onClick={toBooking}>
                {bookCta}
              </Button>
            )}
          </div>
        }
      >
        <div className="flex flex-col gap-lg pt-base pb-base">
          <h1 className="text-h2">Jak przygotować się do spotkania</h1>
          <PrepSteps />
        </div>
      </Shell>
    )
  }

  const plan = lead.plan
  return (
    <Shell
      nav={{}}
      footer={<AskFooter />}
      footerMobileOnly
      aside={
        <StartAside
          booking={last}
          lead={lead}
          durationMin={durationMin}
          nearestStart={nearestStart}
          onBook={toBooking}
          onChange={toChange}
        />
      }
      cta={
        <div className="desktop:hidden">
          {active ? (
            <PlatformEntry booking={active} lead={lead} brief />
          ) : (
            <Button className="w-full" onClick={toBooking}>
              {bookCta}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-base pt-base pb-base">
        <div className="flex flex-col gap-[6px]">
          <h1 className="text-h2">Cześć{lead.firstName ? `, ${lead.firstName}` : ''} 👋</h1>
          <p className="text-label text-text-secondary">
            {active
              ? 'Wszystko gotowe — to Twoje miejsce startu. Wróć tu, kiedy chcesz.'
              : state.kind === 'rebook'
                ? 'Wszystko gotowe — wystarczy wybrać nowy termin bezpłatnego spotkania.'
                : 'Została ostatnia rzecz: wybierz termin bezpłatnego spotkania.'}
          </p>
        </div>
        <div className="desktop:hidden">
          <StatusCard
            booking={last}
            durationMin={durationMin}
            nearestStart={nearestStart}
            onBook={toBooking}
            onChange={toChange}
          />
        </div>
        <MeetingDemoCard />
        <ActionTiles
          onPrepare={() =>
            navigate({ to: '/s/$token', params: { token }, search: { widok: 'przygotowanie' } })
          }
          onChat={() => navigate({ to: '/chat' })}
        />
        {plan ? <ProfileCard rows={buildProfile(lead.quizAnswers, plan)} /> : null}
        <PermaLinkNote />
        {/* Desktop repeats the booking CTA at the end of the column — the aside sits far to the right. */}
        {active ? null : (
          <div className="hidden desktop:block">
            <Button className="w-full" onClick={toBooking}>
              {bookCta}
            </Button>
          </div>
        )}
      </div>
    </Shell>
  )
}
